/**
 * RaiseVariationDialog
 * --------------------
 * Smart router for "I want to change the products on this customer's job".
 * Inspects the contract pack state via /customers/{id}/variation-context and
 * presents one of three flows:
 *
 *   action: 'no_contract'  -> "No contract on file" — open Contract Pack Wizard
 *   action: 'pending'      -> Pack sent/draft but unsigned. Offer:
 *        (a) Edit Contract  → open Contract Pack Wizard
 *        (b) Mark Signed in DocHub → flips pack to fully_signed externally,
 *            then opens the existing VariationOfContractDialog
 *   action: 'signed'       -> Open the existing VariationOfContractDialog
 *
 * The dialog itself does not edit equipment — it routes to the right place.
 * Equipment is edited inside the wizard (for in-flight packs) or via the
 * variation dialog's existing equipment-diff UI (for signed packs).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, FileSignature, AlertTriangle, CheckCircle2, Edit3, ArrowRight, ExternalLink, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

const gbp = (n) => `£${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const RaiseVariationDialog = ({ open, onOpenChange, customerId, currentTotal, customerEmail, onSuccess, onOpenWizard, onOpenVariation }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [marking, setMarking] = useState(false);

  // Manual before/after figures flow (works even with no contract on file)
  const [manualMode, setManualMode] = useState(false);
  const [beforeAmt, setBeforeAmt] = useState('');
  const [afterAmt, setAfterAmt] = useState('');
  const [reason, setReason] = useState('');
  const [emailToCustomer, setEmailToCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    setManualMode(false);
    setReason('');
    setAfterAmt('');
    setBeforeAmt(currentTotal != null ? String(Number(currentTotal).toFixed(2)) : '');
    setEmailToCustomer(false);
    setLoading(true);
    api.get(`/customers/${customerId}/variation-context`)
      .then((r) => setContext(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to inspect contract state'))
      .finally(() => setLoading(false));
  }, [open, customerId, currentTotal]);

  const startManual = () => {
    if (!window.confirm(
      'Raise a variation without a formal contract on file?\n\n' +
      'You\'ll enter the BEFORE and AFTER contract figures and we\'ll generate a ' +
      'Variation of Contract for the difference. Continue?'
    )) return;
    setManualMode(true);
  };

  const diff = (Number(afterAmt) || 0) - (Number(beforeAmt) || 0);

  const handleSubmitManual = async () => {
    if (afterAmt === '' || isNaN(Number(afterAmt))) { toast.error('Enter the new (after) contract figure'); return; }
    if (diff === 0) { toast.error('Before and after figures are the same — nothing to vary'); return; }
    setSubmitting(true);
    try {
      const before = Number(beforeAmt) || 0;
      const line = {
        description: reason.trim() || 'Contract variation',
        add: diff > 0 ? Math.round(diff * 100) / 100 : 0,
        deduct: diff < 0 ? Math.round(-diff * 100) / 100 : 0,
      };
      await api.post(`/customers/${customerId}/variation-of-contract`, {
        reasons: reason.trim() || 'Manual variation (before/after figures)',
        original_contract_sum: before,
        vat_rate: 0,
        lines: [line],
        action: (emailToCustomer && customerEmail) ? 'both' : 'save',
      });
      toast.success((emailToCustomer && customerEmail) ? 'Variation raised & emailed to customer' : 'Variation raised');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to raise variation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditContract = () => {
    onOpenChange(false);
    if (onOpenWizard) onOpenWizard();
  };

  const handleOpenVariation = () => {
    onOpenChange(false);
    if (onOpenVariation) onOpenVariation();
  };

  const handleMarkSignedDochub = async () => {
    const packId = context?.pending_pack?.id;
    if (!packId) return;
    if (!window.confirm(
      'Mark this contract as signed externally via DocHub?\n\n' +
      'This will:\n' +
      ' • Flip the contract pack to "fully signed" with a DocHub flag\n' +
      ' • Snapshot the current equipment as the agreed baseline\n' +
      ' • Open the Variation of Contract dialog so you can record the equipment change\n\n' +
      'Only do this if the customer actually signed the contract in DocHub.'
    )) return;
    setMarking(true);
    try {
      await api.post(`/customers/${customerId}/contract-pack/${packId}/mark-signed-dochub`, {});
      toast.success('Contract marked as signed via DocHub');
      onOpenChange(false);
      if (onOpenVariation) onOpenVariation();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark signed');
    } finally {
      setMarking(false);
    }
  };

  const handleOpenContractWizard = () => {
    onOpenChange(false);
    navigate(`/customers/${customerId}?openWizard=1`);
    if (onOpenWizard) onOpenWizard();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="raise-variation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-amber-600" />
            Raise Variation / Update Contract
          </DialogTitle>
          <DialogDescription>
            We'll route you based on the current contract state.
          </DialogDescription>
        </DialogHeader>

        {manualMode ? (
          <div className="space-y-4 py-2" data-testid="manual-variation-form">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
              <Calculator className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Enter the agreed contract value before and after the change. We'll generate a Variation of Contract for the difference{currentTotal == null ? '' : ' (current total pre-filled)'}.</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="var-before">Before (£)</Label>
                <Input id="var-before" type="number" step="0.01" value={beforeAmt}
                  onChange={(e) => setBeforeAmt(e.target.value)} placeholder="0.00"
                  data-testid="variation-before-input" />
              </div>
              <div>
                <Label htmlFor="var-after">After (£)</Label>
                <Input id="var-after" type="number" step="0.01" value={afterAmt}
                  onChange={(e) => setAfterAmt(e.target.value)} placeholder="0.00"
                  data-testid="variation-after-input" />
              </div>
            </div>
            <div className={`rounded-md px-3 py-2 text-sm font-medium ${diff > 0 ? 'bg-emerald-50 text-emerald-700' : diff < 0 ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-500'}`} data-testid="variation-diff">
              Variation value: {diff > 0 ? '+' : ''}{gbp(diff)} {diff > 0 ? '(additional charge)' : diff < 0 ? '(credit / reduction)' : ''}
            </div>
            <div>
              <Label htmlFor="var-reason">Reason / description</Label>
              <Textarea id="var-reason" rows={2} value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Added 4 extra panels at customer's request"
                data-testid="variation-reason-input" />
            </div>
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${customerEmail ? 'text-slate-700' : 'text-slate-400 cursor-not-allowed'}`}>
              <input type="checkbox" checked={emailToCustomer} disabled={!customerEmail}
                onChange={(e) => setEmailToCustomer(e.target.checked)} data-testid="variation-email-customer" />
              Email the Variation of Contract to the customer for signature
              {!customerEmail && <span className="text-xs">(no email on file)</span>}
            </label>
            <label className="flex items-start gap-2 text-xs text-slate-500">
              <span>The difference will be applied to this customer's final payment automatically (no separate invoice is raised, avoiding double-billing).</span>
            </label>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setManualMode(false)} disabled={submitting}>Back</Button>
              <Button onClick={handleSubmitManual} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-manual-variation">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSignature className="w-4 h-4 mr-2" />}
                Raise Variation
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Inspecting contract pack…
          </div>
        ) : context && (
          <div className="space-y-4 py-2">
            {context.action === 'no_contract' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3" data-testid="action-no-contract">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">No contract pack on file</p>
                    <p className="text-sm text-blue-700 mt-1">
                      You can still raise a variation. Either generate a formal contract via the Contract Pack Wizard, or enter the before/after contract figures manually to create a variation straight away.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleOpenContractWizard}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid="open-wizard-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Open Contract Pack Wizard
                  </Button>
                  <Button
                    onClick={startManual}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="choice-manual-variation"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Enter figures manually
                  </Button>
                </div>
                <p className="text-[11px] text-blue-700/80 italic">
                  &ldquo;Enter figures manually&rdquo; asks you to confirm, then captures the before &amp; after contract values and generates a Variation of Contract for the difference (no formal contract required).
                </p>
              </div>
            )}

            {context.action === 'pending' && (
              <div className="space-y-3" data-testid="action-pending">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-900">Contract pack is out for signing but not yet signed</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Pack <Badge variant="outline" className="text-xs ml-1">{context.pending_pack?.pack_type || 'contract'}</Badge>
                        {' '}sent on {fmt(context.pending_pack?.sent_at || context.pending_pack?.created_at)}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={handleEditContract}
                    className="text-left rounded-lg border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50 p-4 transition"
                    data-testid="choice-edit-contract"
                  >
                    <div className="flex items-center gap-3">
                      <Edit3 className="w-5 h-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">Edit &amp; resend contract</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Customer hasn't signed yet — modify the products in the wizard and re-send. No variation needed.
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </button>

                  <button
                    onClick={handleMarkSignedDochub}
                    disabled={marking}
                    className="text-left rounded-lg border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 p-4 transition disabled:opacity-50"
                    data-testid="choice-mark-signed-dochub"
                  >
                    <div className="flex items-center gap-3">
                      {marking ? (
                        <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                      ) : (
                        <ExternalLink className="w-5 h-5 text-emerald-600" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">Mark as signed in DocHub → raise variation</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Customer signed externally (e.g. DocHub). Flips the pack to "fully signed", snapshots equipment, then opens the Variation of Contract dialog.
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </button>
                </div>
              </div>
            )}

            {context.action === 'signed' && (
              <div className="space-y-3" data-testid="action-signed">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-emerald-900">Contract is fully signed</p>
                      <p className="text-sm text-emerald-700 mt-1">
                        Pack signed on {fmt(context.signed_pack?.admin_signed_at || context.signed_pack?.customer_signed_at)}
                        {context.signed_pack?.signed_externally ? ' (externally via DocHub)' : ''}.
                        Equipment changes from this point must go through a Variation of Contract.
                      </p>
                    </div>
                  </div>
                </div>
                <Button onClick={handleOpenVariation} className="bg-amber-600 hover:bg-amber-700" data-testid="open-variation-btn">
                  <FileSignature className="w-4 h-4 mr-2" />
                  Open Variation of Contract
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RaiseVariationDialog;
